import * as React from "react";

const DEFAULT_LONG_PRESS_MS = 500;
const MOVE_CANCEL_PX = 8;

export interface LongPressHandlers {
  onPointerDown: (e: React.PointerEvent) => void;
  onPointerMove: (e: React.PointerEvent) => void;
  onPointerUp: (e: React.PointerEvent) => void;
  onPointerLeave: (e: React.PointerEvent) => void;
  onPointerCancel: (e: React.PointerEvent) => void;
  /**
   * Wrap a short-tap handler so a long-press never ALSO fires it. Attach the
   * result as the element's `onClick`, alongside this hook's pointer handlers:
   *
   *   const longPress = useLongPress(() => openEditSheet(item));
   *   <button {...longPress} onClick={longPress.wrapTap(() => toggle(item))} />
   *
   * Contract (mirrors StepDot in ProgressBar.tsx): pointerdown starts a `ms`
   * timer. If nothing cancels it first, it fires `onLongPress` immediately —
   * NOT on pointerup/click — and flags that a long-press just happened. The
   * browser still delivers a `click` after the pointerup that ends that
   * gesture; `wrapTap`'s returned handler checks the flag on that click,
   * swallows it (preventDefault + stopPropagation, no `onTap` call) and
   * clears the flag. A genuine short tap never sets the flag, so `wrapTap`
   * calls `onTap` normally.
   */
  wrapTap: (onTap: () => void) => (e: React.MouseEvent) => void;
}

/**
 * Pointer-based long-press detector, generalized from the `StepDot` pattern
 * in `features/cook-sessions/ProgressBar.tsx`. Starts a `ms` (default 500)
 * timer on pointerdown; fires `onLongPress` when it elapses. Cancelled by:
 * pointerup/leave/cancel before the threshold, or the pointer moving more
 * than 8px from its pointerdown position (`Math.hypot(dx, dy) > 8`) — so a
 * scroll/drag never mis-fires a long-press. The timer is also cleared on
 * unmount.
 *
 * Does not fire a tap action itself — pair with `wrapTap` (see above) if the
 * caller wants a short-tap action that a long-press must not also trigger.
 */
export function useLongPress(
  onLongPress: () => void,
  opts?: { ms?: number },
): LongPressHandlers {
  const ms = opts?.ms ?? DEFAULT_LONG_PRESS_MS;
  const pressTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const didLongPress = React.useRef(false);
  const start = React.useRef<{ x: number; y: number } | null>(null);

  // Unmount cleanup — `pressTimer` is a stable ref object, so this reads its
  // live `.current` at cleanup time, not a value captured at mount.
  React.useEffect(() => {
    return () => {
      if (pressTimer.current !== null) {
        clearTimeout(pressTimer.current);
      }
    };
  }, []);

  function clearPress(): void {
    if (pressTimer.current !== null) {
      clearTimeout(pressTimer.current);
      pressTimer.current = null;
    }
  }

  function onPointerDown(e: React.PointerEvent): void {
    // Only react to primary button (left-click / touch), mirroring StepDot.
    if (e.pointerType === "mouse" && e.button !== 0) return;
    didLongPress.current = false;
    start.current = { x: e.clientX, y: e.clientY };
    pressTimer.current = setTimeout(() => {
      didLongPress.current = true;
      pressTimer.current = null;
      onLongPress();
    }, ms);
  }

  function onPointerMove(e: React.PointerEvent): void {
    if (!start.current || pressTimer.current === null) return;
    const dx = e.clientX - start.current.x;
    const dy = e.clientY - start.current.y;
    if (Math.hypot(dx, dy) > MOVE_CANCEL_PX) {
      clearPress();
    }
  }

  function onPointerUp(): void {
    clearPress();
    start.current = null;
  }

  function onPointerLeave(): void {
    clearPress();
    start.current = null;
  }

  function onPointerCancel(): void {
    clearPress();
    start.current = null;
  }

  function wrapTap(onTap: () => void): (e: React.MouseEvent) => void {
    return (e: React.MouseEvent) => {
      if (didLongPress.current) {
        didLongPress.current = false;
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      onTap();
    };
  }

  return {
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerLeave,
    onPointerCancel,
    wrapTap,
  };
}
